// utils
import ModelMapperApi from '@utils/modelMapperApi.compose'
import _flatMap from 'lodash/flatMap'
import cloneDeep from 'lodash/cloneDeep'
import logger from '@/telemetry'

// services
import { WroService } from '@services/receiving'
import { ProductService } from '@/services/product/index'
import { PricingPlanService } from '@services/pricing'
import { ShipmentService } from '@/services/orderDetails/shipment'
import { mapAddressToValidationApi } from '@/mappers/receiving/draft/packagingType'
import { CreateWarehouseReceivingOrderService } from '@/services/createWarehouseReceivingOrder/index'
import { ShipOptionsService } from '@/services/shipOptions'
import { InventoryDistributionService } from '@services/inventoryDistribution'

// models
import { WroDraft } from '@/models/receiving/draft'
import { WroPackagingType } from '@/models/receiving/draft/packagingType'
import { WroSkuType } from '@/models/receiving/draft/skuType'
import { WroLabelPurchaseType } from '@/models/receiving/draft/labelPurchaseType'
import { WroOriginAddress } from '@/models/receiving/draft/originAddress'
import { WroDraftProductQuantityWithId, WroDraftProduct } from '@/models/receiving/draft/product'
import { ShippingLabelPricing } from '@/models/receiving/draft/shippingLabelPricing'
import { InventoryDestinations } from '@models/inventoryDistribution'

// mappers
import WroDraftMapper from '@/mappers/receiving/draft'
import { PlatformSourceMapper } from '@mappers'
import { mapPropertyOrDefault } from '@mappers/mapping-helper'
import PricingPlanMapper from '@/mappers/pricing/pricing'
import { ShippingLabelPricingByShipOption } from '@/models/receiving/draft/shippingLabelPricingByShipOption'
import { SystemSettingsService } from '@/services/systemSettings'
import { mapSdsStatus } from '@/utils/productPropertiesMapping'
import InventoryDistributionMapper from '@mappers/inventoryDistribution'

// modules
export const ModuleName = 'createWarehouseReceivingOrder'

const findProductAndIndex = (products, id) => {
  const productIndex = products.map(p => p.id).indexOf(id)
  const product = products[productIndex]
  return { productIndex, product }
}

const updateProduct = (ps, p) => ps.splice(findProductAndIndex(ps, p.id).productIndex, 1, p)

const findQuantitiesToSendAndIndex = (qts, id) => {
  const quantityIndex = qts.map(f => f._id).indexOf(id)
  const quantityToSend = qts[quantityIndex]
  return { quantityIndex, quantityToSend }
}

const updateQuantitiesToSend = (qts, q) => qts.splice(findQuantitiesToSendAndIndex(qts, q._id).quantityIndex, 1, q)

const updateProductQuantityField = ({ products, productId, fieldId, fieldKey, fieldVal }) => {
  const { product } = findProductAndIndex(products, productId)
  const qts = product.quantitiesToSend
  const { quantityToSend } = findQuantitiesToSendAndIndex(qts, fieldId)
  quantityToSend[fieldKey] = fieldVal
  updateQuantitiesToSend(qts, quantityToSend)
  updateProduct(products, product)
}

const updateShippingConfigurations = (shippingConfigurations, id, boxConfigurations) => {
  shippingConfigurations[id].boxConfigurations = boxConfigurations
  shippingConfigurations.splice(id, 1, shippingConfigurations[id])
}

export default {
  namespaced: true,
  state: {
    draft: new WroDraft(),
    fcChange: false,
    filteredProducts: [],
    stepCollapsed: false,
    spokesForHubId: [],
    selectedProductsEdited: false,
    shippingLabelPricing: new ShippingLabelPricing(),
    shippingLabelPricingByShipOption: new ShippingLabelPricingByShipOption(),
    editWro: new WroDraft(),
    toggleProductIdsToLot: [],
    toggleProductIdsToNotLot: [],
    toggleProductIdsAreDangerous: [],
    toggleProductIdsAreNotDangerous: [],
    shippingServices: [],
    sendDirectEnabled: '',
    inventoryDistributionDestinations: [],
    currentInventoryDistributionDestinations: []
  },
  mutations: {
    setFlagForChange (state, payload) {
      state[payload.flag] = payload.value
    },
    resetDraft (state) {
      state.draft = new WroDraft()
      state.warnings = undefined
    },
    setDraftId (state, id) {
      state.draft.id = id
    },
    setState (state, currentState) {
      state.draft.currentStep = currentState
    },
    resetFc (state) {
      state.draft.fulfillmentCenter = null
    },
    changeAutoDistributeToggle (state, newVal) {
      state.draft.autoDistributeToggle = newVal
    },
    setSpokesForHubId (state, spokesForHubId) {
      state.spokesForHubId = spokesForHubId
    },
    setDraft (state, draft) {
      const existingFc = state.draft.fulfillmentCenter
      state.draft = draft
      if (existingFc) {
        state.draft.fulfillmentCenter = existingFc
      }

      if (state.draft.finalDestinationFulfillmentCenterId) {
        state.draft.destinationFulfillmentCenter = { id: state.draft.finalDestinationFulfillmentCenterId }
      }
    },
    setEditWro (state, draft) {
      state.editWro = draft
    },
    addToggleProductIdsToLot (state, id) {
      if (state.toggleProductIdsToNotLot.indexOf(id) < 0) {
        state.toggleProductIdsToLot.push(id)
      } else {
        state.toggleProductIdsToNotLot.splice(state.toggleProductIdsToNotLot.indexOf(id), 1)
      }
    },
    addToggleProductIdsToNotLot (state, id) {
      if (state.toggleProductIdsToLot.indexOf(id) < 0) {
        state.toggleProductIdsToNotLot.push(id)
      } else {
        state.toggleProductIdsToLot.splice(state.toggleProductIdsToLot.indexOf(id), 1)
      }
    },
    addToggleProductIdIsDangerous (state, id) {
      if (state.toggleProductIdsAreNotDangerous.indexOf(id) < 0) {
        state.toggleProductIdsAreDangerous.push(id)
      } else {
        state.toggleProductIdsAreNotDangerous.splice(state.toggleProductIdsAreNotDangerous.indexOf(id), 1)
      }
    },
    addToggleProductIdIsNotDangerous (state, id) {
      if (state.toggleProductIdsAreDangerous.indexOf(id) < 0) {
        state.toggleProductIdsAreNotDangerous.push(id)
      } else {
        state.toggleProductIdsAreDangerous.splice(state.toggleProductIdsAreDangerous.indexOf(id), 1)
      }
    },
    removeToggleProductIdsLotNotLot (state, id) {
      if (state.toggleProductIdsToLot.indexOf(id) !== -1) {
        state.toggleProductIdsToLot.splice(state.toggleProductIdsToLot.indexOf(id), 1)
      }
      if (state.toggleProductIdsToNotLot.indexOf(id) !== -1) {
        state.toggleProductIdsToNotLot.splice(state.toggleProductIdsToNotLot.indexOf(id), 1)
      }
    },
    removeToggleProductDangerousStatus (state, id) {
      if (state.toggleProductIdsAreDangerous.indexOf(id) !== -1) {
        state.toggleProductIdsAreDangerous.splice(state.toggleProductIdsAreDangerous.indexOf(id), 1)
      }
      if (state.toggleProductIdsAreNotDangerous.indexOf(id) !== -1) {
        state.toggleProductIdsAreNotDangerous.splice(state.toggleProductIdsAreNotDangerous.indexOf(id), 1)
      }
    },
    clearToggleProductIdsToLot (state) {
      state.toggleProductIdsToLot = []
    },
    clearToggleProductIdsToNotLot (state) {
      state.toggleProductIdsToNotLot = []
    },
    clearToggleProductIdsAreDangerous (state) {
      state.toggleProductIdsAreDangerous = []
    },
    clearToggleProductIdsAreNotDangerous (state) {
      state.toggleProductIdsAreNotDangerous = []
    },
    setFc (state, fc) {
      state.draft.fulfillmentCenter = fc
    },
    setDestinationFc (state, fc) {
      state.draft.destinationFulfillmentCenter = fc
    },
    setPurchaseOrderNumber (state, pon) {
      state.draft.purchaseOrderNumber = pon
    },
    resetPurchaseOrderNumber (state) {
      state.draft.purchaseOrderNumber = ''
    },
    setSelectedProducts (state, products) {
      state.draft.products = products
    },
    setSelectedPackagingType (state, packagingType) {
      state.draft.packagingType = packagingType
    },
    resetSelectedPackagingType (state) {
      state.draft.packagingType = new WroPackagingType()
    },
    setSelectedSKUType (state, skuType) {
      state.draft.skuType = skuType
    },
    resetSelectedSKUType (state) {
      state.draft.skuType = new WroSkuType()
    },
    setSelectedLabelPurchaseType (state, labelPurchaseType) {
      state.draft.labelPurchaseType = labelPurchaseType
    },
    setWarnings (state, warnings) {
      state.warnings = warnings
    },
    resetSelectedLabelPurchaseType (state) {
      state.draft.labelPurchaseType = new WroLabelPurchaseType()
    },
    setOriginAddress (state, originAddress) {
      state.draft.originAddress = originAddress
    },
    resetOriginAddress (state) {
      state.draft.originAddress = new WroOriginAddress()
    },
    resetEstimatedDateOfArrival (state) {
      state.draft.estimatedArrivalDate = ''
    },
    resetBillOfLading (state) {
      state.draft.billOfLading.file = null
    },
    addQuantity (state, id) {
      const products = state.draft.products
      const { product } = findProductAndIndex(products, id)
      product.quantitiesToSend.push(new WroDraftProductQuantityWithId())
      updateProduct(products, product)
    },
    removeQuantity (state, { productId, fieldId, shouldRemoveProduct }) {
      const products = state.draft.products
      const { product } = findProductAndIndex(products, productId)
      const quantityToRemove = product.quantitiesToSend.find(qts => qts._id === fieldId)
      if (quantityToRemove === undefined) return
      product.quantitiesToSend = product.quantitiesToSend.filter(qts => qts._id !== fieldId)
      if (shouldRemoveProduct) product.quantitiesToSend.push(new WroDraftProductQuantityWithId())
      updateProduct(products, product)
    },
    setFilteredProducts (state, products) {
      state.filteredProducts = products
    },
    updateQuantity (state, { productId, fieldId, quantity }) {
      updateProductQuantityField({
        products: state.draft.products,
        productId,
        fieldId,
        fieldKey: 'amount',
        fieldVal: quantity
      })
    },
    updateLotNumber (state, { productId, fieldId, lotNumber }) {
      updateProductQuantityField({
        products: state.draft.products,
        productId,
        fieldId,
        fieldKey: 'lotNumber',
        fieldVal: lotNumber
      })
    },
    updateLotDate (state, { productId, fieldId, lotDate }) {
      updateProductQuantityField({
        products: state.draft.products,
        productId,
        fieldId,
        fieldKey: 'expirationDate',
        fieldVal: lotDate
      })
    },
    clearProducts (state) {
      state.draft.products = []
      state.filteredProducts = state.filteredProducts.map(fp => {
        fp.quantitiesToSend = []
        fp.quantitiesToSend.push(new WroDraftProductQuantityWithId())
        return fp
      })
    },
    completeStep (state, stepNumber) {
      state.draft.furthestCompletedStep = Math.max(state.draft.furthestCompletedStep, stepNumber)
    },
    setStepCollapsed (state, collapsed) {
      state.stepCollapsed = collapsed
    },
    updateQuantityPerBox (state, { id, boxConfigId, quantityPerBox }) {
      const shippingConfigurations = state.draft.shippingConfigurations
      const boxConfigurations = shippingConfigurations[id].boxConfigurations
      boxConfigurations[boxConfigId].quantityPerBox = quantityPerBox
      updateShippingConfigurations(shippingConfigurations, id, boxConfigurations)
    },
    updateNumberOfBoxes (state, { id, boxConfigId, numberOfBoxes }) {
      const shippingConfigurations = state.draft.shippingConfigurations
      const boxConfigurations = shippingConfigurations[id].boxConfigurations
      boxConfigurations[boxConfigId].numberOfBoxes = numberOfBoxes
      updateShippingConfigurations(shippingConfigurations, id, boxConfigurations)
    },
    updateBoxConfigurations (state, { id, boxConfigId, boxConfiguration }) {
      const shippingConfigurations = state.draft.shippingConfigurations
      const boxConfigurations = shippingConfigurations[id].boxConfigurations
      boxConfigurations[boxConfigId] = boxConfiguration
      updateShippingConfigurations(shippingConfigurations, id, boxConfigurations)
    },
    addBoxConfiguration (state, id) {
      const shippingConfigurations = state.draft.shippingConfigurations
      shippingConfigurations[id].add()
    },
    removeBoxConfiguration (state, { id, boxConfigId }) {
      const shippingConfigurations = state.draft.shippingConfigurations
      const boxConfigurations = shippingConfigurations[id].boxConfigurations
      boxConfigurations.splice(boxConfigId, 1)
      updateShippingConfigurations(shippingConfigurations, id, boxConfigurations)
    },
    initShippingConfigurations (state, shippingConfigurations) {
      state.draft.initializeShippingConfigurations(shippingConfigurations)
    },
    clearShippingConfigurationValues (state) {
      state.draft.clearShippingConfigurationValues()
    },
    setEstimatedArrivalDate (state, estimatedArrivalDate) {
      state.draft.estimatedArrivalDate = estimatedArrivalDate
    },
    setPricingGroup (state, pricingGroup) {
      state.draft.pricingGroup = pricingGroup
    },
    setBillOfLading (state, bol) {
      state.draft.billOfLading.file = bol
    },
    setFurthestCompletedStep (state, step) {
      state.draft.furthestCompletedStep = step
    },
    setSelectedProductsEdited (state, hasBeenEdited) {
      state.selectedProductsEdited = hasBeenEdited
    },
    setShippingLabelPricing (state, shippingLabelPricing) {
      state.shippingLabelPricing = shippingLabelPricing
    },
    setShippingLabelPricingByShipOption (state, shippingLabelPricing) {
      state.shippingLabelPricingByShipOption = shippingLabelPricing
    },
    resetDraftWro (state) {
      state.draft = new WroDraft()
    },
    addSkuConfigurationById (state, skuConfigId) {
      state.draft.addSkuConfigurationById(skuConfigId)
    },
    removeSkuConfigurationById (state, ids) {
      state.draft.removeSkuConfigurationById(ids)
    },
    addMultiSkuBoxConfiguration (state, data) {
      state.draft.addMultiSkuBoxConfiguration(data)
    },
    updateMultiSKUConfiguration (state, { boxConfigId, skuConfigId, skuConfig }) {
      state.draft.updateSkuConfigurationById(boxConfigId, skuConfigId, skuConfig)
    },
    selectProductForMultiSkuConfigurationById (state, data) {
      state.draft.selectProductForMultiSkuConfigurationById(data)
    },
    selectQuantityForMultiSkuConfigurationById (state, data) {
      state.draft.selectQuantityForMultiSkuConfigurationById(data)
    },
    selectNumberOfBoxesForMultiSkuConfigurationById (state, data) {
      state.draft.selectNumberOfBoxesForMultiSkuConfigurationById(data)
    },
    clearMultiSkuShippingConfigurationValues (state) {
      state.draft.clearMultiSkuShippingConfigurationValues()
    },
    setShippingServices (state, shippingServices) {
      state.shippingServices = shippingServices
    },
    setSendDirectEnabled (state, sendDirectEnabled) {
      state.sendDirectEnabled = sendDirectEnabled
    },
    setDestinationSpokesForHubId (state, spokesForHubId) {
      state.inventoryDistributionDestinations.push(spokesForHubId)
    },
    setCurrentInventoryDistributionDestinations (state, destinations) {
      state.currentInventoryDistributionDestinations = destinations
    }
  },
  actions: {
    async submitWro ({ dispatch }, draft) {
      const func = ModelMapperApi.post(
        ModelMapperApi.service(CreateWarehouseReceivingOrderService.submitWro, CreateWarehouseReceivingOrderService),
        ModelMapperApi.mapper(WroDraftMapper.mapToApiForCreate)
      )
      try {
        const pending = func(draft)
        dispatch('addPromise', { promise: pending, scopes: ['submittingWro'] }, { root: true })
        const response = await pending
        await dispatch('completeWro', draft.id)
        return {
          success: true,
          response
        }
      } catch (ex) {
        return {
          success: false
        }
      }
    },
    async submitWroByShipOption ({ dispatch }, draft) {
      const func = ModelMapperApi.post(
        ModelMapperApi.service(CreateWarehouseReceivingOrderService.submitWro, CreateWarehouseReceivingOrderService),
        ModelMapperApi.mapper(WroDraftMapper.mapToApiForCreateByShipOption)
      )
      try {
        const pending = func(draft)
        dispatch('addPromise', { promise: pending, scopes: ['submittingWro'] }, { root: true })
        const response = await pending
        await dispatch('completeWro', draft.id)
        return {
          success: true,
          response
        }
      } catch (ex) {
        return {
          success: false
        }
      }
    },
    async getSpokesForHubId ({ commit, dispatch }, hubId) {
      try {
        const pending = WroService.getSpokesForHubId(hubId)
        dispatch('addPromise', { promise: pending, scopes: ['spokesLoading'] }, { root: true })
        const spokes = await pending
        commit('setSpokesForHubId', spokes)
      } catch (ex) {
        logger?.trackException({
          exception: ex,
          severityLevel: 3
        }, {
          custom: 'getSpokesForHubId'
        })
        return false
      }
    },
    async completeWro ({ dispatch }, draftId) {
      const func = ModelMapperApi.post(
        ModelMapperApi.service(CreateWarehouseReceivingOrderService.completeWro, CreateWarehouseReceivingOrderService),
        pass => pass
      )
      try {
        const pending = func(draftId)
        dispatch('addPromise', { promise: pending, scopes: ['submittingWro'] }, { root: true })
        const response = await pending
        return {
          success: true,
          response
        }
      } catch (ex) {
        return {
          success: false
        }
      }
    },
    async validateAddressAndSave ({ commit, dispatch }) {
      const func = ModelMapperApi.post(
        ModelMapperApi.service(ShipmentService.validateAddress, ShipmentService),
        ModelMapperApi.mapper(mapAddressToValidationApi)
      )
      try {
        const pending = func(this.state.createWarehouseReceivingOrder.draft.originAddress)
        dispatch('addPromise', { promise: pending, scopes: ['savingWro'] }, { root: true })
        const response = await pending
        return {
          success: true,
          response
        }
      } catch (ex) {
        return {
          success: false
        }
      }
    },
    async upsertWro ({ commit, dispatch }, { mapper, service, draft }) {
      const func = ModelMapperApi.post(
        ModelMapperApi.service(service, CreateWarehouseReceivingOrderService),
        ModelMapperApi.mapper(mapper)
      )
      try {
        const pending = func(draft)
        dispatch('addPromise', { promise: pending, scopes: ['savingWro'] }, { root: true })
        const response = await pending
        if (response.draftId) commit('setDraftId', response.draftId)
        return {
          success: true,
          response
        }
      } catch (ex) {
        return {
          success: false
        }
      }
    },
    setFlagForChange ({ commit }, payload) {
      commit('setFlagForChange', payload)
    },
    setState ({ commit }, currentState) {
      commit('setState', currentState)
    },
    resetFc ({ commit }) {
      commit('resetFc')
    },
    resetSelectedPackagingType ({ commit }) {
      commit('resetSelectedPackagingType')
    },
    resetSelectedSKUType ({ commit }) {
      commit('resetSelectedSKUType')
    },
    resetSelectedLabelPurchaseType ({ commit }) {
      commit('resetSelectedLabelPurchaseType')
    },
    resetOriginAddress ({ commit }) {
      commit('resetOriginAddress')
    },
    resetEstimatedDateOfArrival ({ commit }) {
      commit('resetEstimatedDateOfArrival')
    },
    resetBillOfLading ({ commit }) {
      commit('resetBillOfLading')
    },
    setFc ({ commit }, fc) {
      commit('setFc', fc)
    },
    setDestinationFc ({ commit }, fc) {
      commit('setDestinationFc', fc)
    },
    setPurchaseOrderNumber ({ commit }, pon) {
      commit('setPurchaseOrderNumber', pon)
    },
    resetPurchaseOrderNumber ({ commit }) {
      commit('resetPurchaseOrderNumber')
    },
    setSelectedPackagingType ({ commit }, packagingType) {
      commit('setSelectedPackagingType', packagingType)
    },
    setSelectedSKUType ({ commit }, skuType) {
      commit('setSelectedSKUType', skuType)
    },
    setSelectedLabelPurchaseType ({ commit }, labelPurchaseType) {
      commit('setSelectedLabelPurchaseType', labelPurchaseType)
    },
    setOriginAddress ({ commit }, originAddress) {
      commit('setOriginAddress', originAddress)
    },
    async getWroDraft ({ commit, dispatch, rootGetters }, wroDraftId) {
      const wroDraftServiceFunc = CreateWarehouseReceivingOrderService.getWroDraftV2

      const wroDraftFunc = ModelMapperApi.get(
        ModelMapperApi.model(WroDraft),
        ModelMapperApi.mapper(WroDraftMapper.mapFromApi),
        ModelMapperApi.service(wroDraftServiceFunc, CreateWarehouseReceivingOrderService)
      )
      try {
        const wroPromise = wroDraftFunc(wroDraftId)
        dispatch('addPromise', { promise: wroPromise, scopes: ['getWroDraft', 'setupWro'] }, { root: true })
        const wroDraft = await wroPromise
        return wroDraft
      } catch (ex) {
        const defaultWroDraft = new WroDraft()
        return defaultWroDraft
      }
    },
    async getAndSetWroDraft ({ commit, dispatch }, wroDraftId) {
      const wroDraft = await dispatch('getWroDraft', wroDraftId)
      commit('setDraft', wroDraft)
    },
    async getAndSetWroDraftByShipOption ({ commit, dispatch, getters }, wroDraftId) {
      const wroDraft = await dispatch('getWroDraft', wroDraftId, { scopes: ['getWroDraft', 'setupWro'] })
      if (wroDraft.pricingGroup != null) {
        try {
          const mappedBoxes = getters.getWROBoxes(wroDraft)
          // map address for request for quotes
          const mappedAddress = getters.getWROAddress(wroDraft)
          const wroLabelQuotes = await PricingPlanService.getWroLabelQuotesByShipOption({ boxes: mappedBoxes, destinationInformation: mappedAddress }, { scopes: ['getWroDraft', 'setupWro'] })
          const newQuote = wroLabelQuotes.find(w => w.shipOptionId === wroDraft.pricingGroup.id)
          const newPricingGroup = ({
            id: wroDraft.pricingGroup.id,
            name: wroDraft.pricingGroup.name,
            estimatedShippingCost: (newQuote.totalPrice) ? newQuote.totalPrice : 'Exceeds weight limit.',
            shipbobZoneId: wroDraft.pricingGroup.shipbobZoneId,
            currencyCode: newQuote.currencyCode
          })
          commit('setDraft', wroDraft)
          await dispatch('setPricingGroup', newPricingGroup, { scopes: ['getWroDraft', 'setupWro'] })
        } catch (ex) {
          const defaultWroDraft = new WroDraft()
          commit('setDraft', defaultWroDraft)
        }
      } else { commit('setDraft', wroDraft) }
    },
    async getWroToBeEdited ({ commit, dispatch }, wroId) {
      const wroToBeEditedFunc = ModelMapperApi.get(
        ModelMapperApi.model(WroDraft),
        ModelMapperApi.mapper(WroDraftMapper.mapFromApi),
        ModelMapperApi.service(CreateWarehouseReceivingOrderService.getWroToBeEdited, CreateWarehouseReceivingOrderService)
      )
      try {
        const wroPromise = wroToBeEditedFunc(wroId)
        dispatch('addPromise', { promise: wroPromise, scopes: ['getWroDraft', 'setupWro'] }, { root: true })
        const wroDraft = await wroPromise
        commit('setDraft', wroDraft)
        commit('setEditWro', cloneDeep(wroDraft))
      } catch (ex) {
        const defaultWroDraft = new WroDraft()
        commit('setDraft', defaultWroDraft)
        commit('setEditWro', defaultWroDraft)
      }
    },
    async discardWroDraft ({ commit, state, dispatch }) {
      const defaultWroDraft = new WroDraft()
      const wroDraftFunc = ModelMapperApi.post(
        ModelMapperApi.service(CreateWarehouseReceivingOrderService.deleteWroDraft, CreateWarehouseReceivingOrderService),
        pass => pass
      )
      try {
        const wroPromise = wroDraftFunc(state.draft.id)
        dispatch('addPromise', { promise: wroPromise, scopes: ['deletWroDraft'] }, { root: true })
        commit('setDraft', defaultWroDraft)
      } catch (ex) {
        commit('setDraft', defaultWroDraft)
      }
    },
    async getShipbobLabelQuotes ({ commit, state, dispatch }) {
      const wroDraftFunc = ModelMapperApi.post(
        ModelMapperApi.service(PricingPlanService.getWroLabelQuotes, PricingPlanService),
        pass => pass
      )

      try {
        let boxes = []
        const mappedBoxes = []
        // map boxes
        if (state.draft.skuType.id === 1) {
          boxes = state.draft.shippingConfigurations.map(x => x.boxConfigurations)
          // we are flattening out the boxes for a edge case where there is a single sku per box but multiple configurations for single sku
          const flattenedBoxes = [].concat.apply([], boxes)
          flattenedBoxes.forEach(function (box) {
            for (let i = 0; i < box.numberOfBoxes; i++) {
              mappedBoxes.push(PricingPlanMapper.mapWroBoxForLabelQuoteToApi(box))
            }
          })
        } else {
          boxes = state.draft.multiSKUShippingConfigurations.map(x => x.skuConfigurations)
          boxes.forEach(function (box) {
            for (let i = 0; i < box[0].numberOfBoxes; i++) {
              mappedBoxes.push(PricingPlanMapper.mapWroBoxForLabelQuoteToApi(box[0]))
            }
          })
        }
        // map address for request for quotes
        const mappedAddress = PricingPlanMapper.mapWroAddressForLabelQuoteToApi(
          state.draft.originAddress,
          state.draft.fulfillmentCenter.id
        )
        const wroPromise = wroDraftFunc({ boxes: mappedBoxes, destinationInformation: mappedAddress })
        dispatch('addPromise', { promise: wroPromise, loadingScopes: ['getWroPricingQuotes'] }, { root: true })
        const shippingLabelPricing = await wroPromise
        commit('setShippingLabelPricing', shippingLabelPricing.map(x => PricingPlanMapper.mapLabelPricingResponseFromApi(x)))
      } catch (ex) {
        const defaultShippingLabelPricing = new ShippingLabelPricing()
        commit('setShippingLabelPricing', defaultShippingLabelPricing)
      }
    },
    async getShipbobLabelQuotesByShipOption ({ commit, state, dispatch, getters }) {
      try {
        const mappedBoxes = getters.getWROBoxes(state.draft)
        // map address for request for quotes
        const mappedAddress = getters.getWROAddress(state.draft)
        const wroLabelQuotes = await PricingPlanService.getWroLabelQuotesByShipOption({ boxes: mappedBoxes, destinationInformation: mappedAddress })
        commit('setShippingLabelPricingByShipOption', wroLabelQuotes.map(x => PricingPlanMapper.mapLabelPricingByShipOptionResponseFromApi(x)))
      } catch (ex) {
        const defaultShippingLabelPricing = new ShippingLabelPricing()
        commit('setShippingLabelPricingByShipOption', defaultShippingLabelPricing)
      }
    },
    async getAllShippingServices ({ commit }) {
      try {
        const shippingServices = await ShipOptionsService.getShippingServices()
        commit('setShippingServices', shippingServices)
      } catch (ex) {
        commit('setShippingServices', [])
      }
    },
    setSelectedProducts ({ commit }, products) {
      commit('setSelectedProducts', products)
    },
    addQuantity ({ commit }, id) {
      commit('addQuantity', id)
    },
    async removeQuantity ({ commit, state }, { productId, fieldId }) {
      const { product } = findProductAndIndex(state.draft.products, productId)
      const markProductAsNotLot = product.quantitiesToSend.length <= 1
      const shouldRemoveProduct = false
      if (markProductAsNotLot) {
        product.isLotProduct = false
        product.quantitiesToSend[0].lotNumber = ''
        product.quantitiesToSend[0].expirationDate = ''
        commit('setSelectedProducts', state.draft.products)
        commit('addToggleProductIdsToNotLot', productId)
      } else {
        commit('removeQuantity', { productId, fieldId, shouldRemoveProduct })
      }
    },
    async removeProduct ({ commit, state }, { productId }) {
      const { product } = findProductAndIndex(state.draft.products, productId)
      commit('setSelectedProducts', state.draft.products.filter(p => p.id !== product.id))
      commit('removeToggleProductIdsLotNotLot', productId)
      commit('removeToggleProductDangerousStatus', productId)
    },
    async productMarkAsLot ({ commit, state }, { productId, isMarkAsLot }) {
      const { product } = findProductAndIndex(state.draft.products, productId)
      product.isLotProduct = isMarkAsLot
      commit('setSelectedProducts', state.draft.products)
      commit('addToggleProductIdsToLot', productId)
    },
    async productToggleDangerous ({ commit, state }, { productId, isMarkedAsDangerous }) {
      const { product } = findProductAndIndex(state.draft.products, productId)
      product.isDangerousGoods = isMarkedAsDangerous
      commit('setSelectedProducts', state.draft.products)
      const eventName = isMarkedAsDangerous ? 'addToggleProductIdIsDangerous' : 'addToggleProductIdIsNotDangerous'
      commit(eventName, productId)
    },
    async clearToggleProductIdsToLot ({ commit }) {
      commit('clearToggleProductIdsToLot')
    },
    async clearToggleProductIdsToNotLot ({ commit }) {
      commit('clearToggleProductIdsToNotLot')
    },
    async clearToggleProductIdsAreDangerous ({ commit }) {
      commit('clearToggleProductIdsAreDangerous')
    },
    async clearToggleProductIdsAreNotDangerous ({ commit }) {
      commit('clearToggleProductIdsAreNotDangerous')
    },
    updateQuantity ({ commit }, quantityInfo) {
      commit('updateQuantity', quantityInfo)
    },
    updateQuantityPerBox ({ commit }, quantityInfo) {
      commit('updateQuantityPerBox', quantityInfo)
    },
    updateNumberOfBoxes ({ commit }, boxInfo) {
      commit('updateNumberOfBoxes', boxInfo)
    },
    updateBoxConfigurations ({ commit }, boxInfo) {
      commit('updateBoxConfigurations', boxInfo)
    },
    async searchProducts ({ commit, dispatch }, searchTerm) {
      let filteredProducts = []
      try {
        const res = await ProductService.getProductsByNameOrId(searchTerm)
        filteredProducts = res.map(product => {
          const { id, name, platformId, sku, barCode, productStatusAttributeVm, customsInformationVm } = product
          const { isLot, isDangerousGoods } = productStatusAttributeVm
          const { tariffCode, itemValue, itemDescription } = customsInformationVm
          return new WroDraftProduct({
            id,
            name,
            sku,
            platform: {
              id: platformId,
              name: mapPropertyOrDefault(PlatformSourceMapper.mapPlatformSourceFromAPI, platformId).name
            },
            isLotProduct: isLot,
            barcode: barCode,
            isDangerousGoods,
            tariffCode,
            customsValue: itemValue,
            description: itemDescription
          })
        })
      } catch (ex) {
      }
      commit('setFilteredProducts', filteredProducts)
    },
    async searchProductsV2 ({ commit, dispatch }, searchTerm) {
      const filteredProducts = []
      const res = await WroService.getProductsByNameOrIdForCreateV2(searchTerm)
      for (const item of res) {
        for (const subitem of item.variants) {
          const variantMatches = (subitem.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                                   subitem.inventory.inventory_id.toString().includes(searchTerm) ||
                                   subitem.sku.toLowerCase().includes(searchTerm.toLowerCase())) &&
                                   item.type !== 'Bundle' && subitem.status === 'Active' && !subitem.is_digital

          if (!variantMatches) continue

          let platform = {}
          if (subitem.channel_metadata.length > 0) {
            platform = {
              id: subitem.channel_metadata[0].platform_identifier,
              name: subitem.channel_metadata[0].platform
            }
          }
          try {
            filteredProducts.push(new WroDraftProduct({
              id: subitem.inventory?.inventory_id,
              name: subitem.name,
              sku: subitem.sku,
              platform,
              isLotProduct: subitem.lot_information?.is_lot,
              barcode: subitem.barcode,
              isDangerousGoods: subitem.fulfillment_settings?.dangerous_goods,
              sdsStatus: mapSdsStatus(subitem.fulfillment_settings?.dangerous_goods, subitem.fulfillment_settings?.msds_url),
              tariffCode: subitem.customs?.hs_tariff_code,
              customsValue: subitem.customs?.value,
              description: subitem.customs?.description
            }))
          } catch (ex) {
            console.log(`Error processing variant with name ${subitem.name} and sku ${subitem.sku} and error ${ex}`)
          }
        }
      }
      commit('setFilteredProducts', filteredProducts)
    },
    updateLotNumber ({ commit }, lotNumberInfo) {
      commit('updateLotNumber', lotNumberInfo)
    },
    updateLotDate ({ commit }, lotDateInfo) {
      commit('updateLotDate', lotDateInfo)
    },
    clearProducts ({ commit }) {
      commit('clearProducts')
    },
    completeStep ({ commit }, stepNumber) {
      commit('completeStep', stepNumber)
    },
    setStepCollapsed ({ commit }, collapsed) {
      commit('setStepCollapsed', collapsed)
    },
    addBoxConfiguration ({ commit }, id) {
      commit('addBoxConfiguration', id)
    },
    removeBoxConfiguration ({ commit }, configObject) {
      commit('removeBoxConfiguration', configObject)
    },
    initShippingConfigurations ({ commit }, shippingConfigurations) {
      commit('initShippingConfigurations', shippingConfigurations)
    },
    clearShippingConfigurationValues ({ commit }) {
      commit('clearShippingConfigurationValues')
    },
    setEstimatedArrivalDate ({ commit }, estimatedArrivalDate) {
      commit('setEstimatedArrivalDate', estimatedArrivalDate)
    },
    setPricingGroup ({ commit }, pricingGroup) {
      commit('setPricingGroup', pricingGroup)
    },
    setBillOfLading ({ commit }, bol) {
      commit('setBillOfLading', bol)
    },
    async fetchBolFile ({ dispatch, commit }, { url, name }) {
      try {
        // dispatch('addPromise', { promise: fetchBolFile, scopes: ['fetchingBolFile'] }, { root: true })
        // permissions to fetch file are all messed up so just creating a dummy file with the correct name.
        const file = new File([], name, { type: 'application/pdf' })
        commit('setBillOfLading', file)
      } catch (ex) {
      }
    },
    setFurthestCompletedStep ({ commit }, step) {
      commit('setFurthestCompletedStep', step)
    },
    setSelectedProductsEdited ({ commit }, hasBeenEdited) {
      commit('setSelectedProductsEdited', hasBeenEdited)
    },
    setShippingLabelPricing ({ commit }, shippingLabelPricing) {
      commit('setShippingLabelPricing', shippingLabelPricing)
    },
    resetDraft ({ commit }) {
      commit('resetDraft')
    },
    addSkuConfigurationById ({ commit }, boxConfigId) {
      commit('addSkuConfigurationById', boxConfigId)
    },
    removeSkuConfigurationById ({ commit }, ids) {
      commit('removeSkuConfigurationById', ids)
    },
    addMultiSkuBoxConfiguration ({ commit }, data) {
      commit('addMultiSkuBoxConfiguration', data)
    },
    updateMultiSKUConfiguration ({ commit }, data) {
      commit('updateMultiSKUConfiguration', data)
    },
    selectProductForMultiSkuConfigurationById ({ commit }, data) {
      commit('selectProductForMultiSkuConfigurationById', data)
    },
    selectQuantityForMultiSkuConfigurationById ({ commit }, data) {
      commit('selectQuantityForMultiSkuConfigurationById', data)
    },
    selectNumberOfBoxesForMultiSkuConfigurationById ({ commit }, data) {
      commit('selectNumberOfBoxesForMultiSkuConfigurationById', data)
    },
    clearMultiSkuShippingConfigurationValues ({ commit }) {
      commit('clearMultiSkuShippingConfigurationValues')
    },
    autoFillSingleSKUFloorLoadedContainer ({ commit, state, getters }) {
      commit('/clearShippingConfigurationValues')
      // should only by one product
      const selectedProduct = getters.selectedProducts[0]
      const selectedProductsQTS = getters.productQuantitiesToSendByProductId(selectedProduct.id)
      const totalProductToSend = selectedProductsQTS.reduce((acc, n) => { acc += n[0].amount; return acc }, 0)
      commit('updateNumberOfBoxes', { id: 0, boxConfigId: 0, numberOfBoxes: 1 })
      commit('updateQuantityPerBox', { id: 0, boxConfigId: 0, quantityPerBox: totalProductToSend })
    },
    autoFillMultiSKUFloorLoadedContainer ({ commit, state, getters }) {
      commit('clearMultiSkuShippingConfigurationValues')
      const selectedProducts = getters.selectedProductsWithLotInformation
      const multiSkuShippingConfiguration = getters.multiSkuShippingConfigurations[0]

      for (let index = 0; index < selectedProducts.length; index++) {
        const product = selectedProducts[index]
        if (index > 0) {
          commit('addSkuConfigurationById', multiSkuShippingConfiguration.id)
        }
        const productQuantity = product.quantitiesToSend.reduce((acc, n) => { acc += n.amount; return acc }, 0)
        commit('selectProductForMultiSkuConfigurationById', { boxConfigId: multiSkuShippingConfiguration.id, product, skuConfigId: index })
        commit('selectQuantityForMultiSkuConfigurationById', { boxConfigId: multiSkuShippingConfiguration.id, value: productQuantity, skuConfigId: index })
      }
      commit('selectNumberOfBoxesForMultiSkuConfigurationById', { skuConfigId: 0, boxConfigId: multiSkuShippingConfiguration.id, value: 1 })
    },
    async getProductWarnings (_, obj) {
      const func = ModelMapperApi.post(
        ModelMapperApi.service(CreateWarehouseReceivingOrderService.getProductWarnings, CreateWarehouseReceivingOrderService),
        pass => pass
      )
      try {
        const pending = func(obj)
        const response = await pending
        return {
          success: true,
          products: response
        }
      } catch (ex) {
        return {
          success: false,
          errorMessage: `Import failed with error: ${ex.message}. If this happens again, please contact support.`
        }
      }
    },
    async getSendDirectSetting ({ commit, dispatch }, { loadingScopes }) {
      try {
        const pending = SystemSettingsService.get('ShowSendDirect')
        dispatch('addPromise', { promise: pending, scopes: [...loadingScopes, 'getSendDirectSetting'] }, { root: true })
        const setting = await pending
        commit('setSendDirectEnabled', setting)
      } catch (ex) {
        logger?.trackException({
          exception: ex,
          severityLevel: 3
        }, {
          custom: 'sendDirectLoading'
        })
      }
    },
    async getInventoryDistributionDestinations ({ commit, state, dispatch }, hubId) {
      try {
        const destinationsForHubId = state.inventoryDistributionDestinations.find(x => x.hubFcId === hubId)
        if (destinationsForHubId) {
          commit('setCurrentInventoryDistributionDestinations', destinationsForHubId.spokes)
          return
        }

        const getAvailableDestinationsServiceFunc = InventoryDistributionService.getAvailableWroDestinationsForSourceHub

        const getAvailableDestinationsFunc = ModelMapperApi.get(
          ModelMapperApi.model(InventoryDestinations),
          ModelMapperApi.mapper(InventoryDistributionMapper.mapInventoryDestinationsFromApi),
          ModelMapperApi.service(getAvailableDestinationsServiceFunc, InventoryDistributionService)
        )
        const destinationsPromise = getAvailableDestinationsFunc(hubId)
        dispatch('addPromise', { promise: destinationsPromise, scopes: ['destinationsLoading'] }, { root: true })
        const inventoryDestinations = await destinationsPromise
        const spokes = Object.values(inventoryDestinations)
        commit('setDestinationSpokesForHubId', {
          hubFcId: hubId,
          spokes
        })

        commit('setCurrentInventoryDistributionDestinations', spokes)
      } catch (ex) {
        console.log('error while retrieving inventory destinations', ex)
        logger?.trackException({
          exception: ex,
          severityLevel: 3
        }, {
          custom: 'getInventoryDistributionDestinations'
        })
      }
    }
  },
  getters: {
    draft: state => state.draft,
    fcChange: state => state.fcChange,
    editWro: state => state.editWro,
    currentStep: state => state.draft.currentStep,
    furthestCompletedStep: state => state.draft.furthestCompletedStep,
    purchaseOrderNumber: state => state.draft.purchaseOrderNumber,
    fulfillmentCenter: state => state.draft.fulfillmentCenter, // but it is the same one???
    destinationFulfillmentCenter: state => state.draft.destinationFulfillmentCenter,
    getAutoDistributeToggleState: state => state.draft.autoDistributeToggle,
    selectedProducts: state => state.draft.products,
    filteredProducts: state => state.filteredProducts,
    stepCollapsed: state => state.stepCollapsed,
    getSpokesForHubId: state => state.spokesForHubId,
    shippingConfigurations: state => state.draft.shippingConfigurations,
    multiSkuShippingConfigurations: state => state.draft.multiSKUShippingConfigurations,
    selectedPackagingType: state => state.draft.packagingType,
    selectedSKUType: state => state.draft.skuType,
    selectedLabelPurchaseType: state => state.draft.labelPurchaseType,
    originAddress: state => state.draft.originAddress,
    estimatedArrivalDate: state => state.draft.estimatedArrivalDate,
    pricingGroup: state => state.draft.pricingGroup,
    billOfLading: state => state.draft.billOfLading,
    selectedProductsWithLotInformation: state => {
      const products = state.draft.products || []
      return _flatMap(products.map(sp => sp.quantitiesToSend.map(qts => ({
        ...sp,
        lotInformation: {
          lotNumber: qts.lotNumber,
          expirationDate: qts.expirationDate
        }
      }))))
    },
    wroContainsProductsWithMultipleLots: (state, getters) => getters.selectedProducts.length > 0 && getters.selectedProducts.length !== getters.selectedProductsWithLotInformation.length,
    floorLoadedContainerWithNoMultipleLots: (state, getters) => getters.selectedPackagingType && getters.selectedPackagingType.id === 3 && !getters.wroContainsProductsWithMultipleLots,
    productQuantitiesToSendByProductId: (state, getters) => (productId) => {
      return getters.selectedProducts
        .filter(p => p.id === productId)
        .map(p => p.quantitiesToSend)
    },
    label: state => state.draft.pricingGroup && state.draft.pricingGroup.name,
    estimatedShippingCost: state => state.draft.pricingGroup && state.draft.pricingGroup.estimatedShippingCost,
    shippingCurrencyCode: state => state.draft.pricingGroup && state.draft.pricingGroup.currencyCode,
    isShipbobLabels: state => (state.draft.labelPurchaseType && !state.draft.products.some(x => x.isDangerousGoods)) ? (state.draft.labelPurchaseType.id === 1) : false,
    getShippingLabelPricing: state => state.shippingLabelPricing,
    getShippingLabelPricingByShipOption: state => state.shippingLabelPricingByShipOption,
    isWroCreated: state => state.draft.wroCreated,
    toggleProductIdsLot: state => state.toggleProductIdsToLot,
    toggleProductIdsNotLot: state => state.toggleProductIdsToNotLot,
    toggleProductIdsAreDangerous: state => state.toggleProductIdsAreDangerous,
    toggleProductIdsAreNotDangerous: state => state.toggleProductIdsAreNotDangerous,
    warnings: state => {
      return state.warnings
    },
    getShippingServices: state => state.shippingServices,
    getWROAddress: () => (wroDraft) => {
      return PricingPlanMapper.mapWroAddressForLabelQuoteToApi(
        wroDraft.originAddress,
        wroDraft.fulfillmentCenter.id
      )
    },
    getWROBoxes: () => (wroDraft) => {
      let boxes = []
      const mappedBoxes = []
      // map boxes
      if (wroDraft.skuType.id === 1) {
        boxes = wroDraft.shippingConfigurations.map(x => x.boxConfigurations)
        // we are flattening out the boxes for a edge case where there is a single sku per box but multiple configurations for single sku
        const flattenedBoxes = [].concat.apply([], boxes)
        flattenedBoxes.forEach(function (box) {
          for (let i = 0; i < box.numberOfBoxes; i++) {
            mappedBoxes.push(PricingPlanMapper.mapWroBoxForLabelQuoteToApi(box))
          }
        })
      } else {
        boxes = wroDraft.multiSKUShippingConfigurations.map(x => x.skuConfigurations)
        boxes.forEach(function (box) {
          for (let i = 0; i < box[0].numberOfBoxes; i++) {
            mappedBoxes.push(PricingPlanMapper.mapWroBoxForLabelQuoteToApi(box[0]))
          }
        })
      }
      return mappedBoxes
    },
    getSendDirectEnabled: state => state.sendDirectEnabled,
    inventoryDistributionDestinationsForHub: state => state.currentInventoryDistributionDestinations
  }
}
